import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { formatCurrency, formatOdds } from '../../utils';
import type { Fight, Participant } from '../../types';

interface ParticipantBetHistoryProps {
  participant: Participant;
  fights: Fight[];
  onClose: () => void;
}

/**
 * Participant Bet History Component
 * Displays past bets (won/lost only, no pending) for a specific participant
 */
export function ParticipantBetHistory({ participant, fights, onClose }: ParticipantBetHistoryProps) {
  // Filter to only show won/lost bets (no pending)
  const pastBets = participant.bets.filter(bet => bet.status === 'won' || bet.status === 'lost');

  const getFightName = (fightId: string): string => {
    if (fightId === 'parlay') return 'Parlay Bet';
    const fight = fights.find((f) => f.id === fightId);
    if (!fight) return 'Unknown Fight';
    return fight.fighters.map((f) => f.name).join(' vs ');
  };

  const getFighterName = (fightId: string, fighterId?: string): string => {
    if (!fighterId) return '';
    const fight = fights.find((f) => f.id === fightId);
    if (!fight) return 'Unknown Fighter';
    const fighter = fight.fighters.find((f) => f.id === fighterId);
    return fighter?.name || 'Unknown Fighter';
  };

  const getFightResult = (fightId: string): string | null => {
    const fight = fights.find((f) => f.id === fightId);
    if (!fight || !fight.result) return null;
    
    const result = fight.result;
    const winner = fight.fighters.find(f => f.id === result.winnerId);
    const winnerName = winner?.name || 'Unknown';
    
    let methodStr = '';
    switch (result.method) {
      case 'ko_tko': methodStr = 'KO/TKO'; break;
      case 'submission': methodStr = 'Sub'; break;
      case 'decision_unanimous': methodStr = 'UD'; break;
      case 'decision_split': methodStr = 'SD'; break;
      case 'decision_majority': methodStr = 'MD'; break;
      case 'dq': methodStr = 'DQ'; break;
      case 'draw': return 'Draw';
      case 'no_contest': return 'No Contest';
      default: methodStr = result.method;
    }
    
    if (result.wentTheDistance) {
      return `${winnerName} by ${methodStr}`;
    } else if (result.round && result.timeInRound) {
      return `${winnerName} by ${methodStr} (R${result.round}, ${result.timeInRound})`;
    }
    return `${winnerName} by ${methodStr}`;
  };

  const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Sort bets by date (most recent first)
  const sortedBets = [...pastBets].sort((a, b) => b.placedAt - a.placedAt);

  // Calculate summary stats
  const wonBets = pastBets.filter(b => b.status === 'won');
  const lostBets = pastBets.filter(b => b.status === 'lost');
  const totalWinnings = wonBets.reduce((sum, bet) => sum + (bet.payout || 0), 0);
  const totalLost = lostBets.reduce((sum, bet) => sum + bet.amount, 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card padding="lg" className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-primary-text">{participant.name}'s Bet History</h2>
            <p className="text-sm text-secondary-text mt-1">Past bets only (won/lost)</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-tertiary-bg rounded-lg p-4 border border-border">
            <div className="text-xs text-secondary-text mb-1">Total Past Bets</div>
            <div className="text-2xl font-bold text-primary-text">{pastBets.length}</div>
          </div>
          <div className="bg-tertiary-bg rounded-lg p-4 border border-success-green">
            <div className="text-xs text-secondary-text mb-1">Won</div>
            <div className="text-2xl font-bold text-success-green">{wonBets.length}</div>
          </div>
          <div className="bg-tertiary-bg rounded-lg p-4 border border-error">
            <div className="text-xs text-secondary-text mb-1">Lost</div>
            <div className="text-2xl font-bold text-error-red">{lostBets.length}</div>
          </div>
          <div className="bg-tertiary-bg rounded-lg p-4 border border-border">
            <div className="text-xs text-secondary-text mb-1">Net Result</div>
            <div className={`text-2xl font-bold ${
              totalWinnings - totalLost > 0 ? 'text-success-green' :
              totalWinnings - totalLost === 0 ? 'text-secondary-text' :
              'text-error-red'
            }`}>
              {formatCurrency(totalWinnings - totalLost)}
            </div>
          </div>
        </div>

        {/* Bet List */}
        {sortedBets.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-secondary-text">No past bets to show</p>
            <p className="text-muted-text text-sm mt-2">
              Only completed bets (won/lost) are displayed
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedBets.map((bet) => {
              const fightName = getFightName(bet.fightId);
              const selection = bet.isParlay 
                ? `${bet.parlayLegs?.length || 0}-Leg Parlay`
                : bet.fighterId
                ? getFighterName(bet.fightId, bet.fighterId)
                : (bet.outcomeName || 'Prop Bet');

              return (
                <div
                  key={bet.id}
                  className={`p-4 bg-tertiary-bg rounded-lg border-2 ${
                    bet.status === 'won' 
                      ? 'border-success-green bg-success-green/5' 
                      : 'border-error/30 bg-error/5'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-semibold text-base text-primary-text">{selection}</div>
                        <Badge variant={bet.status === 'won' ? 'success' : 'error'} size="sm">
                          {bet.status === 'won' ? 'Won' : 'Lost'}
                        </Badge>
                      </div>
                      <div className="text-sm text-secondary-text">{fightName}</div>
                      <div className="text-xs text-muted-text mt-1">
                        Placed: {formatTimestamp(bet.placedAt)}
                      </div>
                      {!bet.isParlay && getFightResult(bet.fightId) && (
                        <div className="text-xs text-muted-text">
                          Result: {getFightResult(bet.fightId)}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-border">
                    <div>
                      <div className="text-xs text-secondary-text mb-1">Bet Amount</div>
                      <div className="font-semibold text-primary-text">
                        {formatCurrency(bet.amount)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-secondary-text mb-1">Odds</div>
                      <div className="font-semibold text-primary-text">
                        {formatOdds(bet.odds)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-secondary-text mb-1">
                        {bet.status === 'won' ? 'Payout' : 'Lost'}
                      </div>
                      <div className={`font-semibold text-base ${
                        bet.status === 'won' ? 'text-success-green' : 'text-error-red'
                      }`}>
                        {bet.status === 'won' ? formatCurrency(bet.payout || 0) : `-${formatCurrency(bet.amount)}`}
                      </div>
                    </div>
                  </div>

                  {/* Parlay Legs */}
                  {bet.isParlay && bet.parlayLegs && bet.parlayLegs.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="text-xs font-semibold text-secondary-text mb-2">Parlay Legs:</div>
                      <div className="space-y-1">
                        {bet.parlayLegs.map((leg, index) => {
                          const legResult = (leg.status === 'won' || leg.status === 'lost') 
                            ? getFightResult(leg.fightId) 
                            : null;
                          return (
                            <div 
                              key={index}
                              className="text-xs text-secondary-text px-2 py-1 bg-secondary-bg rounded"
                            >
                              <div className="flex items-center justify-between">
                                <span>
                                  {leg.fightName}: {leg.selection} ({leg.odds > 0 ? '+' : ''}{leg.odds})
                                </span>
                                {leg.status && (
                                  <span className={`ml-2 ${
                                    leg.status === 'won' ? 'text-success-green' : 
                                    leg.status === 'lost' ? 'text-error-red' : 
                                    'text-warning-yellow'
                                  }`}>
                                    {leg.status === 'won' ? '✓' : leg.status === 'lost' ? '✗' : '⏳'}
                                  </span>
                                )}
                              </div>
                              {legResult && (
                                <div className="text-muted-text mt-0.5">
                                  Result: {legResult}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

